import { Link } from 'react-router-dom';
import {
  BoardroomMode,
  Brand,
  BuyersGuideChecklist,
  FreshnessStamp,
  marketingCopy,
} from '@aigovops/design-system';
import { roster } from '../data/editors';

/**
 * /research — public research page.
 *
 * Serves the "forward this internally" motion for Nadia and Priya per
 * docs/landing-strategy.md D3. v1 scope (OD2 resolution):
 * - GRC pain synthesis, republished as HTML with named source list
 * - Embedded BuyersGuideChecklist (shared component with Landing)
 *
 * Market map deferred to v2 pending legal review on naming competitors.
 * See docs/landing-strategy.md for the v2 constraint.
 */

const RESEARCH_FRESHNESS = {
  updatedAt: '2026-08-18T00:00:00Z',
  source: 'GRC pain synthesis · 12 forum + survey sources',
  reviewer: roster,
};

interface PainPoint {
  n: string;
  title: string;
  severity: string;
  summary: string;
  data: string[];
  perspective: string;
}

const painPoints: PainPoint[] = [
  {
    n: '01',
    title: 'Shadow AI & Inventory Blindness',
    severity: 'Severity 8–9',
    summary:
      "You can't govern what you can't see. The scale of unsanctioned AI use has outpaced every traditional discovery mechanism.",
    data: [
      'Only 13% of IT/security pros are fully confident they can see every AI tool employees use (NHI Management).',
      '54% of employees say they would use AI tools to work faster even if it bypasses company policy (Onspring).',
      'Over 80% of workers use unapproved AI tools outside official channels (Trussed AI).',
      'Only 25% of organizations have comprehensive visibility into employee AI use.',
    ],
    perspective:
      'Cybersecurity practitioners on r/cybersecurity describe AI tool adoption as happening bottom-up, evading oversight until data exfiltration surfaces in logs.',
  },
  {
    n: '02',
    title: 'Fragmented Ownership & Accountability Vacuum',
    severity: 'Severity 6–7',
    summary:
      'No one owns AI governance, so everyone assumes someone else does. This is the structural issue practitioners complain about most.',
    data: [
      'No single function owns more than 25% of AI governance responsibility.',
      '71% of organizations report an AI tool used for GRC has led to a failed audit or lapsed standard at least once.',
      '66% of board directors report limited to no knowledge or experience with AI.',
      "54% of directors say emerging tech disruption isn't a standing board agenda item.",
    ],
    perspective:
      'GRC is perceived as "separate and isolated from those who do the real work." Compliance teams write AI policies engineering never reads; legal drafts terms procurement doesn\'t enforce on vendors. The result is compliance theatre.',
  },
  {
    n: '03',
    title: 'Regulatory Complexity, Velocity & Unpreparedness',
    severity: 'Severity 8–9',
    summary:
      'The regulatory landscape is moving faster than GRC teams can adapt, and deadlines are already arriving.',
    data: [
      'EU AI Act enforcement began August 2, 2026 — penalties up to €35M or 7% of global revenue.',
      '78% of enterprises are unprepared for EU AI Act obligations (Optro).',
      '61% of compliance teams experience regulatory complexity + resource fatigue (Governance Intelligence).',
      '2 in 3 organizations take between 1 week and 6 months to implement controls in response to AI risks (Sprinto).',
    ],
    perspective:
      'A "whack-a-mole" dynamic — EU AI Act, US state laws, sector-specific rules, evolving standards (ISO 42001, NIST AI RMF) all move on different timelines with different requirements. Comparisons to early GDPR chaos are common.',
  },
  {
    n: '04',
    title: 'Tooling & Maturity Gap — Spreadsheets vs. AI-Speed Risk',
    severity: 'Severity 7–8',
    summary:
      'GRC teams are trying to govern AI-speed risks with pre-AI tools and processes.',
    data: [
      '60% of GRC users still manage compliance manually using spreadsheets (Trussed AI).',
      'Only 19% of organizations can identify cross-functional risks in real time.',
      '43% of professionals say AI tools meant to simplify GRC actually made their jobs more difficult.',
      '90% report at least some AI investments fell short of expectations.',
    ],
    perspective:
      'Practitioners flag a "governance gap we can no longer ignore" — the delta between AI capability deployed and governance infrastructure in place. The skills gap compounds it: teams need AI literacy that\'s a significant departure from traditional GRC expertise.',
  },
  {
    n: '05',
    title: 'Third-Party / Vendor AI Risk & Model Drift',
    severity: 'Severity 7–8',
    summary:
      "The AI risk surface extends far beyond what organizations build themselves — and it changes after initial assessment.",
    data: [
      'Only 21% of organizations report mature governance models for agentic AI, even as 75% plan to deploy it within two years.',
      '34% cite employees inputting sensitive data into AI tools as their top risk concern.',
      '40% reported inaccurate AI outputs in the past 12 months; 22% faced legal claims tied to AI use.',
      'AI-enabled social engineering increased 61% year-over-year.',
    ],
    perspective:
      '"A policy can say employees should not paste confidential data into public models. It cannot, by itself, discover that they already are." (Onspring). Continuous monitoring of vendor AI behavior is operationally difficult and often not part of existing TPRM programs.',
  },
];

const sources: { label: string; href: string }[] = [
  { label: 'GRC Efficiency Gap: AI and Trust (Onspring)', href: 'https://onspring.com/resources/blog/grc-efficiency-gap-ai-trust-maturity/' },
  { label: 'State of GRC 2026: Accountability Gap (Drata)', href: 'https://drata.com/blog/state-of-grc-2026' },
  { label: 'Closing the AI Oversight Gap (Optro)', href: 'https://optro.ai/blog/closing-the-ai-oversight-gap' },
  { label: 'Gartner: AI Governance Platforms Market', href: 'https://www.gartner.com/en/newsroom/press-releases/2026-02-17-gartner-global-ai-regulations-fuel-billion-dollar-market-for-ai-governance-platforms' },
  { label: 'Grant Thornton: AI Proof Gap Survey', href: 'https://www.grantthornton.com/insights/press-releases/2026/april/grant-thornton-survey-on-ai-proof-gap' },
  { label: 'GRC in 2026: Frameworks & AI Strategy (UnderDefense)', href: 'https://underdefense.com/blog/governance-risk-compliance/' },
];

export function Research() {
  return (
    <BoardroomMode>
      <div style={{ background: 'var(--bg)', color: 'var(--text)', minHeight: '100dvh' }}>
        <header
          style={{
            borderBottom: '1px solid var(--border)',
            background: 'var(--surface)',
          }}
        >
          <div
            style={{
              maxWidth: '80rem',
              margin: '0 auto',
              padding: 'var(--space-4) var(--space-6)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
            }}
          >
            <Link
              to="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-2)',
                textDecoration: 'none',
              }}
            >
              <Brand variant="glyph" />
              <Brand variant="wordmark" />
            </Link>
            <nav
              style={{
                flex: 1,
                display: 'flex',
                gap: 'var(--space-4)',
                justifyContent: 'flex-end',
                fontSize: 'var(--font-size-2xs)',
                fontFamily: 'var(--font-ui)',
              }}
            >
              <Link to="/" style={{ color: 'var(--text-muted)' }}>Home</Link>
              <Link to="/editors" style={{ color: 'var(--text-muted)' }}>Masthead</Link>
            </nav>
          </div>
        </header>

        {/* Hero */}
        <section
          style={{
            padding: 'var(--space-16) var(--space-6)',
            maxWidth: '52rem',
            margin: '0 auto',
            display: 'grid',
            gap: 'var(--space-4)',
            fontFamily: 'var(--font-ui)',
          }}
        >
          <FreshnessStamp {...RESEARCH_FRESHNESS} />
          <h1
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              fontSize: 'clamp(2rem, 6.5vw, var(--font-size-3xl))',
              lineHeight: 'var(--line-height-display)',
              color: 'var(--text)',
              letterSpacing: '-0.01em',
            }}
          >
            The questions we'd want a peer to ask us.
          </h1>
          <p
            style={{
              margin: 0,
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              maxWidth: 'min(48ch, 100%)',
            }}
          >
            The AI GRC category is loud, fragmented, and full of retrofits. We publish the primary
            research and the buyer's-eval questions we'd use ourselves — even when the answer sends
            you somewhere else. Every claim on this page is footnoted; nothing is our marketing.
          </p>
        </section>

        {/* Pain points */}
        <section
          aria-labelledby="pain-heading"
          style={{
            padding: 'var(--space-8) var(--space-6) var(--space-16)',
            maxWidth: '80rem',
            margin: '0 auto',
            fontFamily: 'var(--font-ui)',
          }}
        >
          <header style={{ marginBottom: 'var(--space-8)', maxWidth: '52rem' }}>
            <h2
              id="pain-heading"
              style={{
                margin: 0,
                fontFamily: 'var(--font-display)',
                fontStyle: 'italic',
                fontSize: 'clamp(1.75rem, 5vw, var(--font-size-2xl))',
                color: 'var(--text)',
              }}
            >
              Top five pains for GRC teams governing AI.
            </h2>
            <p style={{ margin: 'var(--space-3) 0 0', color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
              Synthesized from Reddit, industry forums, practitioner communities, and surveys through
              August 2026. Severity ratings reflect aggregate community and survey sentiment; sources
              at the bottom of this page.
            </p>
          </header>
          <ol
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'grid',
              gap: 'var(--space-4)',
              gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
            }}
          >
            {painPoints.map((p) => (
              <li
                key={p.n}
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-card)',
                  padding: 'var(--space-5)',
                  background: 'var(--surface)',
                  display: 'grid',
                  gap: 'var(--space-3)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-3)' }}>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--font-size-3xs)',
                      color: 'var(--text-dim)',
                    }}
                  >
                    {p.n}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-ui)',
                      fontSize: 'var(--font-size-3xs)',
                      color: 'var(--text-dim)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {p.severity}
                  </span>
                </div>
                <h3
                  style={{
                    margin: 0,
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--font-size-md)',
                    color: 'var(--text)',
                  }}
                >
                  {p.title}
                </h3>
                <p style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>{p.summary}</p>
                <ul
                  style={{
                    margin: 0,
                    padding: 'var(--space-2) 0 0 var(--space-4)',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-2xs)',
                    display: 'grid',
                    gap: 'var(--space-2)',
                  }}
                >
                  {p.data.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
                <p
                  style={{
                    margin: 'var(--space-2) 0 0',
                    padding: 'var(--space-3) 0 0',
                    borderTop: '1px dashed var(--border)',
                    color: 'var(--text-muted)',
                    fontSize: 'var(--font-size-2xs)',
                    fontStyle: 'italic',
                  }}
                >
                  {p.perspective}
                </p>
              </li>
            ))}
          </ol>
          <div
            style={{
              marginTop: 'var(--space-8)',
              padding: 'var(--space-5)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-card)',
              background: 'var(--surface-elevated)',
              maxWidth: '52rem',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-md)',
                color: 'var(--text)',
              }}
            >
              Cross-cutting theme — the Dual Role Trap.
            </h3>
            <p style={{ margin: 'var(--space-3) 0 0', color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
              GRC teams are expected to serve simultaneously as enablers of AI adoption (helping the
              business move fast) and guardians against AI risk. These roles create inherent
              tension, and most organizations have not resolved how to balance them structurally.
              Any tool that makes one role easier at the cost of the other is not solving the real
              problem.
            </p>
          </div>
        </section>

        {/* Buyer's guide */}
        <BuyersGuideChecklist
          heading={marketingCopy.buyersGuideHeading}
          lede={marketingCopy.buyersGuideLede}
          questions={marketingCopy.buyersGuideQuestions}
          pdfHref={marketingCopy.buyersGuidePdfHref}
        />

        {/* Sources */}
        <section
          aria-labelledby="sources-heading"
          style={{
            padding: 'var(--space-12) var(--space-6)',
            maxWidth: '52rem',
            margin: '0 auto',
            fontFamily: 'var(--font-ui)',
            borderTop: '1px solid var(--border)',
          }}
        >
          <h2
            id="sources-heading"
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--text)',
            }}
          >
            Sources.
          </h2>
          <ul
            style={{
              margin: 'var(--space-4) 0 0',
              padding: 0,
              listStyle: 'none',
              display: 'grid',
              gap: 'var(--space-2)',
              fontSize: 'var(--font-size-2xs)',
            }}
          >
            {sources.map((s) => (
              <li key={s.href}>
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--text-muted)', textDecoration: 'underline', textUnderlineOffset: '2px' }}
                >
                  {s.label} ↗
                </a>
              </li>
            ))}
          </ul>
          <p
            style={{
              marginTop: 'var(--space-4)',
              color: 'var(--text-dim)',
              fontSize: 'var(--font-size-3xs)',
            }}
          >
            Reviewed by S. Nielsen (GRC lead). See <Link to="/editors" style={{ color: 'var(--text-dim)' }}>the editorial masthead</Link>.
          </p>
        </section>

        <footer
          style={{
            borderTop: '1px solid var(--border)',
            padding: 'var(--space-8) var(--space-6)',
            background: 'var(--surface)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-dim)',
          }}
        >
          <div style={{ maxWidth: '80rem', margin: '0 auto', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-3)' }}>
            <span>© 2026 gacpro · gacpro.io · hello@gacpro.io</span>
            <FreshnessStamp {...RESEARCH_FRESHNESS} />
          </div>
        </footer>
      </div>
    </BoardroomMode>
  );
}
